import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Bottom sheet for the phone screens: focus moves in on open and back to the
 * trigger on close, and Escape or a tap on the backdrop closes it.
 */
export function PhoneSheet({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      trigger?.focus();
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end bg-black/40" data-milestone-sheet>
      <button type="button" aria-label="Close" tabIndex={-1} className="flex-1" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="max-h-[85dvh] space-y-2 overflow-y-auto rounded-t-2xl bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 outline-none"
      >
        {children}
      </div>
    </div>
  );
}
